/**
 * Dashboard card registry — single source of truth for every card on the
 * Portal dashboard. Pure metadata: no React, no DB types.
 *
 * Adding a new card:
 *   1. Add an entry to DASHBOARD_CARDS below.
 *   2. Add a resolver in src/server/controllers/portal/dashboard-card-resolvers.ts.
 *   3. Add a component in src/client/views/portal/views/dashboard/cards/.
 */

export type CardSize = 'small' | 'big'
export type CardRoleGate = ReadonlyArray<'admin' | 'manager' | 'viewer'>

export interface DashboardCardDef {
    key: string
    size: CardSize
    roles: CardRoleGate
    scopeAware: boolean
    titleI18n: string
    descriptionI18n: string
    /**
     * Company-mandated cards render in a separate top "black box" container the
     * user can't add to or delete from (only resize/reorder within it). They
     * also appear conditionally — see CARD_IS_ACTIVE on the client.
     */
    isMandatory?: boolean
    /**
     * The card's resolver blocks on a live external API (Harvest, Notion,
     * Life360) on every request, so its resolve time is unpredictable (can be
     * seconds). The client fetches each of these cards in its own /payload
     * request so it never holds up the fast DB-backed batch — see
     * buildPayloadBatches on the client.
     */
    slowResolver?: boolean
}

export const DASHBOARD_CARDS = {
    // -- Featured cards (seeded first) --
    balancetime: {
        key: 'balancetime',
        size: 'big',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        slowResolver: true, // live Harvest + Humaans round-trips
        titleI18n: 'portal.dashboard.cards.balancetime.title',
        descriptionI18n: 'portal.dashboard.cards.balancetime.description',
    },
    leave_balance: {
        key: 'leave_balance',
        size: 'big',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        titleI18n: 'portal.dashboard.cards.leave_balance.title',
        descriptionI18n: 'portal.dashboard.cards.leave_balance.description',
    },
    // -- Existing widgets ported as cards (v1) --
    headcount: {
        key: 'headcount',
        size: 'big',
        roles: ['admin'],
        scopeAware: true,
        titleI18n: 'portal.dashboard.cards.headcount.title',
        descriptionI18n: 'portal.dashboard.cards.headcount.description',
    },
    my_tasks: {
        key: 'my_tasks',
        size: 'big',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        slowResolver: true, // live Notion task query
        titleI18n: 'portal.dashboard.cards.my_tasks.title',
        descriptionI18n: 'portal.dashboard.cards.my_tasks.description',
    },
    tenure: {
        key: 'tenure',
        size: 'small',
        roles: ['admin'],
        scopeAware: true,
        titleI18n: 'portal.dashboard.cards.tenure.title',
        descriptionI18n: 'portal.dashboard.cards.tenure.description',
    },
    next_7_days_events: {
        key: 'next_7_days_events',
        size: 'big',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: true,
        titleI18n: 'portal.dashboard.cards.next_7_days_events.title',
        descriptionI18n: 'portal.dashboard.cards.next_7_days_events.description',
    },
    team_workload: {
        key: 'team_workload',
        size: 'big',
        roles: ['admin', 'manager'],
        scopeAware: true,
        titleI18n: 'portal.dashboard.cards.team_workload.title',
        descriptionI18n: 'portal.dashboard.cards.team_workload.description',
    },
    team_norm: {
        key: 'team_norm',
        size: 'big',
        roles: ['admin', 'manager'],
        // Always the viewer's own team(s) — keeps the per-member Harvest
        // fan-out bounded, unlike the scope-aware team_workload card.
        scopeAware: false,
        slowResolver: true, // live per-member Harvest fan-out
        titleI18n: 'portal.dashboard.cards.team_norm.title',
        descriptionI18n: 'portal.dashboard.cards.team_norm.description',
    },
    my_tenure: {
        key: 'my_tenure',
        size: 'small',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        titleI18n: 'portal.dashboard.cards.my_tenure.title',
        descriptionI18n: 'portal.dashboard.cards.my_tenure.description',
    },
    // -- App summary cards (Worktime / Playtime / Leaderboard) --
    worktime: {
        key: 'worktime',
        size: 'small',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        titleI18n: 'portal.dashboard.cards.worktime.title',
        descriptionI18n: 'portal.dashboard.cards.worktime.description',
    },
    eat_debt: {
        key: 'eat_debt',
        size: 'small',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        titleI18n: 'portal.dashboard.cards.eat_debt.title',
        descriptionI18n: 'portal.dashboard.cards.eat_debt.description',
    },
    leaderboard: {
        key: 'leaderboard',
        size: 'big',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        titleI18n: 'portal.dashboard.cards.leaderboard.title',
        descriptionI18n: 'portal.dashboard.cards.leaderboard.description',
    },
    // -- Organized events (Üritused) — empty until the events data source exists --
    next_internal_event: {
        key: 'next_internal_event',
        size: 'small',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        isMandatory: true,
        titleI18n: 'portal.dashboard.cards.next_internal_event.title',
        descriptionI18n: 'portal.dashboard.cards.next_internal_event.description',
    },
    next_event: {
        key: 'next_event',
        size: 'small',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        titleI18n: 'portal.dashboard.cards.next_event.title',
        descriptionI18n: 'portal.dashboard.cards.next_event.description',
    },
    next_joiner_leaver: {
        key: 'next_joiner_leaver',
        size: 'big',
        roles: ['admin'],
        scopeAware: true,
        titleI18n: 'portal.dashboard.cards.next_joiner_leaver.title',
        descriptionI18n: 'portal.dashboard.cards.next_joiner_leaver.description',
    },
    // -- Life360 live location (unofficial API, fetched by the BROWSER — the
    //    server is Cloudflare-blocked). Visible to all portal roles; note it
    //    surfaces circle members' real-time locations AND the bearer token to
    //    anyone who can see the card. --
    life360: {
        key: 'life360',
        size: 'big',
        roles: ['admin', 'manager', 'viewer'],
        scopeAware: false,
        slowResolver: false, // resolver only hands the browser its fetch config
        titleI18n: 'portal.dashboard.cards.life360.title',
        descriptionI18n: 'portal.dashboard.cards.life360.description',
    },
} as const satisfies Record<string, DashboardCardDef>

export type DashboardCardKey = keyof typeof DASHBOARD_CARDS

export const DASHBOARD_CARD_KEYS = Object.keys(DASHBOARD_CARDS) as DashboardCardKey[]

// ── Bento grid spans ─────────────────────────────────────────────────────────
// The dashboard renders as a single 6-column bento grid (collapsing on smaller
// screens). Each user-card carries a column span (width) and a row span
// (height); both are user-adjustable from the card's "…" menu in edit mode.

/** Number of columns in the desktop bento grid. */
export const GRID_COLUMNS = 6

/** Allowed card widths in grid columns: ⅓, ½, ⅔, full. */
export const ALLOWED_COL_SPANS = [2, 3, 4, 6] as const

/** Allowed card heights in grid rows (each row is ~156px). */
export const ALLOWED_ROW_SPANS = [1, 2, 3] as const

/**
 * Default spans for a card, derived from its registry `size`. These map the
 * legacy small/big sizes onto the bento grid: small ≈ ⅓ width × 1 row,
 * big ≈ ½ width × 2 rows.
 */
export const defaultSpansForSize = (size: CardSize): { colSpan: number; rowSpan: number } =>
    size === 'big' ? { colSpan: 3, rowSpan: 2 } : { colSpan: 2, rowSpan: 1 }

/**
 * Coerce a span value to the nearest allowed option, falling back to a default
 * when it isn't one of the allowed values. Used to keep persisted/incoming
 * spans within the supported set rather than rejecting the whole layout.
 */
export const clampSpan = (value: number, allowed: readonly number[], fallback: number): number =>
    allowed.includes(value) ? value : fallback

/**
 * Hardcoded default layout for all 13 cards. New dashboards are seeded in this
 * order with these spans, so the bento always opens the same way.
 *
 * Every card is ⅓ width (colSpan 2), so the desktop grid is a tidy 3-up; the
 * list/chart-heavy cards are 2 rows tall and `grid-auto-flow: row dense`
 * backfills the shorter cards beside them. Cards are grouped into bands:
 * personal essentials → what's coming up → team/company pulse → engagement.
 * The pulse band is role-gated (only seeded for manager/admin), so it
 * collapses away for a regular viewer and dense-packing closes the gap.
 *
 * Cards not listed here are appended after, in registry order, with their
 * size-derived default spans.
 */
export interface DefaultLayoutEntry {
    key: string
    colSpan: number
    rowSpan: number
}

export const DEFAULT_DASHBOARD_LAYOUT: ReadonlyArray<DefaultLayoutEntry> = [
    // Personal essentials (everyone)
    { key: 'balancetime', colSpan: 2, rowSpan: 1 },
    { key: 'leave_balance', colSpan: 2, rowSpan: 1 },
    { key: 'my_tasks', colSpan: 2, rowSpan: 2 },
    { key: 'worktime', colSpan: 2, rowSpan: 1 },
    { key: 'my_tenure', colSpan: 2, rowSpan: 1 },
    { key: 'eat_debt', colSpan: 2, rowSpan: 1 },
    // What's coming up (everyone)
    { key: 'next_7_days_events', colSpan: 2, rowSpan: 2 },
    { key: 'next_internal_event', colSpan: 2, rowSpan: 1 },
    { key: 'next_event', colSpan: 2, rowSpan: 1 },
    // Team & company pulse (manager / admin only)
    { key: 'team_workload', colSpan: 2, rowSpan: 2 },
    { key: 'headcount', colSpan: 2, rowSpan: 2 },
    { key: 'tenure', colSpan: 2, rowSpan: 1 },
    { key: 'next_joiner_leaver', colSpan: 2, rowSpan: 1 },
    // Engagement (everyone)
    { key: 'leaderboard', colSpan: 2, rowSpan: 2 },
]

export const cardsForRole = (role: CardRoleGate[number]): DashboardCardDef[] =>
    DASHBOARD_CARD_KEYS.map((k) => DASHBOARD_CARDS[k]).filter((c) =>
        (c.roles as CardRoleGate).includes(role),
    )

/**
 * Whether a card key is a company-mandated card (rendered in the separate top
 * container, not user-addable/removable). Safe for unknown keys.
 */
export const isMandatoryCard = (key: string): boolean =>
    (DASHBOARD_CARDS as Record<string, DashboardCardDef | undefined>)[key]?.isMandatory === true

/** All mandatory card keys, in registry order. */
export const MANDATORY_CARD_KEYS = DASHBOARD_CARD_KEYS.filter(isMandatoryCard)
